import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MultiColorDisplayOption } from '@vestico/model';

import { useElementSize } from '../../hooks';
import { CustomCSSProperties } from '../../model';
import { SVGImage } from '../SVGImage';
import { CommonColorSelectOption } from './CommonColorSelectOption';
import style from './MultiColorSelectOption.module.css';

export interface MultiColorSelectOptionProps {
  option: MultiColorDisplayOption;
  selected?: boolean;
  didUpdateConfig: () => void;
  hideTitle?: boolean;
}

export const MultiColorSelectOption = ({ option, selected, didUpdateConfig, hideTitle }: MultiColorSelectOptionProps) => {
  const { title, hexColors, imagePath } = option;
  const mainColor = hexColors[0];

  const [didLoadImage, setDidLoadImage] = useState(false);
  const onLoadImage = useCallback(() => setDidLoadImage(true), []);

  const imageRef = useRef<HTMLDivElement>(null);
  const contentDimensions = useElementSize(imageRef, !didLoadImage);

  useEffect(() => {
    setDidLoadImage(false);
  }, [imagePath]);

  const imageStyle = useMemo(() => {
    if (!didLoadImage) {
      return undefined;
    }

    const props: CustomCSSProperties = { height: `${contentDimensions.width}px` };
    for (let index = 0; index < hexColors.length; index++) {
      const element = hexColors[index];
      props[`--color-${index}`] = element;
    }
    return props;
  }, [contentDimensions.width, didLoadImage, hexColors]);

  const gradientStyle = useMemo(() => {
    const { length } = hexColors;
    if (imagePath || length === 0) {
      return undefined;
    }

    const gradientValues = hexColors.map((value, index) => `${value} ${(index / (length - 1)) * 100}%`);
    const props: CustomCSSProperties = { background: `linear-gradient(135deg, ${gradientValues.join(', ')}` };
    return props;
  }, [hexColors, imagePath]);

  return (
    <CommonColorSelectOption title={title} hexColor={mainColor} selected={selected} didUpdateConfig={didUpdateConfig} hideTitle={hideTitle}>
      <div className={clsx(style.root, { [style.loading]: !didLoadImage, [style.gradient]: !imagePath })} style={gradientStyle}>
        {imagePath ? (
          <div className={clsx(style.image)} ref={imageRef} style={imageStyle}>
            <SVGImage name={imagePath} onLoadImage={onLoadImage} />
          </div>
        ) : null}
      </div>
    </CommonColorSelectOption>
  );
};
